import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/api/client";
import type {
  Skill,
  SkillCreate,
  SkillPermission,
  SkillPermissionUpsert,
  SkillUpdate,
  SkillVersion,
} from "@/api/types";

const KEY = ["skills"] as const;
const KEY_PERMS = (skillId: string) => ["skill-permissions", skillId] as const;
const KEY_VERSIONS = (skillId: string) =>
  ["skill-versions", skillId] as const;

export function useSkills(filters: { include_inactive?: boolean } = {}) {
  return useQuery({
    queryKey: [...KEY, filters],
    queryFn: () =>
      api.GET<Skill[]>("/skills", {
        params: { include_inactive: Boolean(filters.include_inactive) },
      }),
  });
}

export function useSkill(skillId: string) {
  return useQuery({
    queryKey: [...KEY, skillId],
    queryFn: () => api.GET<Skill>(`/skills/${skillId}`),
    enabled: Boolean(skillId),
  });
}

export function useCreateSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SkillCreate) => api.POST<Skill>("/skills", payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

export function useUpdateSkill(skillId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: SkillUpdate) =>
      api.PATCH<Skill>(`/skills/${skillId}`, patch),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: KEY_VERSIONS(skillId) });
    },
  });
}

export function useDeactivateSkill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (skillId: string) => api.DELETE<void>(`/skills/${skillId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
    },
  });
}

// --- Permissions ----------------------------------------------------------

export function useSkillPermissions(skillId: string) {
  return useQuery({
    queryKey: KEY_PERMS(skillId),
    queryFn: () =>
      api.GET<SkillPermission[]>(`/skills/${skillId}/permissions`),
    enabled: Boolean(skillId),
  });
}

export function useGrantSkillPermission(skillId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: SkillPermissionUpsert) =>
      api.POST<SkillPermission>(`/skills/${skillId}/permissions`, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY_PERMS(skillId) });
    },
  });
}

export function useRevokeSkillPermission(skillId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (teamId: string) =>
      api.DELETE<void>(`/skills/${skillId}/permissions/${teamId}`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY_PERMS(skillId) });
    },
  });
}

// --- Versions (historico Langfuse) ----------------------------------------

export function useSkillVersions(skillId: string, enabled = true) {
  return useQuery({
    queryKey: KEY_VERSIONS(skillId),
    queryFn: () =>
      api.GET<SkillVersion[]>(`/skills/${skillId}/versions`),
    enabled: Boolean(skillId) && enabled,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

export function useRollbackSkillVersion(skillId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      version,
      commit_message,
    }: {
      version: number;
      commit_message?: string;
    }) =>
      api.POST<Skill>(
        `/skills/${skillId}/versions/${version}/rollback`,
        { commit_message: commit_message ?? null },
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: KEY });
      void qc.invalidateQueries({ queryKey: KEY_VERSIONS(skillId) });
    },
  });
}
